import { capitalize } from '@bloomays-lib/utils.shared';
import { Auth0User } from '@bloomays-lib/types.shared';
import { format } from 'date-fns/format';
import { fr } from 'date-fns/locale/fr';
import {
  MissionPrimeLine,
  MonthlyPrimeHeaderAmounts,
  MonthlyTamPrimeAggregate,
  MonthlyTamPrimeValidationInput,
  TamPrimeLine,
} from '../types/monthlyPrime';

const ELIGIBLE_CRA_STATUSES = ['Validé client', 'Forcé Bloomays'] as const;

const VALIDATION_NON_BLOCKING_CRA_STATUSES = ['Validé client', 'Forcé Bloomays', 'Non travaillé'] as const;

const isMissionPrimeLine = (line: TamPrimeLine): line is MissionPrimeLine => 'craStatus' in line;

export const isEligibleCraStatus = (craStatus: string): boolean =>
  ELIGIBLE_CRA_STATUSES.some((status) => status === craStatus);

export const isValidationNonBlockingCraStatus = (craStatus: string): boolean =>
  VALIDATION_NON_BLOCKING_CRA_STATUSES.some((status) => status === craStatus);

export const isPlainCraStatusDisplay = (craStatus: string): boolean => isValidationNonBlockingCraStatus(craStatus);

export const shouldShowMissionActivitiesLinkForCraStatus = (craStatus: string): boolean =>
  !isPlainCraStatusDisplay(craStatus);

const hasMissionWorkedDays = (line: MissionPrimeLine): boolean =>
  line.normalWorkedDays + line.saturdaysWorkedDays + line.sundayAndholidaysWorkedDays + line.astreinteWorkedDays > 0;

/** Ligne mission dont le CRA bloque le bouton Valider. */
const isMissionPrimeBlockingCraValidation = (line: MissionPrimeLine): boolean => {
  if (isValidationNonBlockingCraStatus(line.craStatus)) {
    return false;
  }
  if (line.craStatus === 'Non créé') {
    return true;
  }
  if (!hasMissionWorkedDays(line)) {
    return false;
  }
  return true;
};

export const formatPrimeEuro = (value: number): string => `${value.toLocaleString('fr-FR')} €`;

export const formatPrimeEuroWithSign = (value: number): string => {
  if (value < 0) {
    return `- ${Math.abs(value).toLocaleString('fr-FR')} €`;
  }
  if (value > 0) {
    return `+ ${value.toLocaleString('fr-FR')} €`;
  }
  return formatPrimeEuro(value);
};

export const splitPrimeMonthLabel = (monthLabel: string): { monthName: string; year: string } => {
  const parts = monthLabel.trim().split(/\s+/);
  if (parts.length <= 1) {
    return { monthName: monthLabel, year: '' };
  }
  const year = parts[parts.length - 1] ?? '';
  const monthName = parts.slice(0, -1).join(' ');
  return { monthName, year };
};

export const formatPrimeRate = (rate: number): string => `${(rate * 100).toLocaleString('fr-FR')} %`;

export const sumLineCommissions = (primes: TamPrimeLine[]): number =>
  primes.reduce((sum, line) => sum + line.monthlyPrime, 0);

export const deriveHeaderAmounts = (aggregate: MonthlyTamPrimeAggregate): MonthlyPrimeHeaderAmounts => ({
  freelanceMarge: aggregate.totalFreelanceMarge,
  billingMarge: aggregate.totalBillingMarge,
  commission: sumLineCommissions(aggregate.primes),
  bonus: aggregate.bonus,
  regularisation: aggregate.regularisation,
  total: aggregate.totalPrime,
});

export const getTamDisplayName = (user: Auth0User | undefined): string => {
  if (!user) {
    return '';
  }
  const given = user.given_name?.trim();
  const family = user.family_name?.trim();
  if (given && family) {
    return `${given} ${family}`;
  }
  if (user.displayName?.trim()) {
    return user.displayName.trim();
  }
  if (user.name?.trim()) {
    return user.name.trim();
  }
  return user.email.trim();
};

/** Preview temporaire — 15 du mois pour éviter les décalages fuseau horaire côté API. */
export const toPrimeReferenceDate = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 15);

export const formatPrimeMonthLabel = (referenceDate: Date): string =>
  capitalize(format(referenceDate, 'MMMM yyyy', { locale: fr }));

export const isPrimeLineError = (line: TamPrimeLine): boolean => line.errorCode != null;

export const hasPrimeLineErrors = (aggregate: MonthlyTamPrimeAggregate): boolean =>
  aggregate.primes.some(isPrimeLineError);

export const hasIneligibleMissionCras = (aggregate: MonthlyTamPrimeAggregate): boolean =>
  aggregate.primes.filter(isMissionPrimeLine).some(isMissionPrimeBlockingCraValidation);

export const canValidateMonthlyPrime = (aggregate: MonthlyTamPrimeAggregate): boolean => {
  if (aggregate.totalPrime <= 0) {
    return false;
  }
  if (hasPrimeLineErrors(aggregate)) {
    return false;
  }
  return !hasIneligibleMissionCras(aggregate);
};

export const mapAggregateToValidationInput = (aggregate: MonthlyTamPrimeAggregate): MonthlyTamPrimeValidationInput => ({
  bonus: aggregate.bonus,
  regularisation: aggregate.regularisation,
  totalFreelanceMarge: aggregate.totalFreelanceMarge,
  totalBillingMarge: aggregate.totalBillingMarge,
  totalMarge: aggregate.totalMarge,
  totalPrime: aggregate.totalPrime,
  totalDeals: aggregate.totalDeals,
  totalMissions: aggregate.totalMissions,
  primes: aggregate.primes.map((line) => {
    const base = {
      jobFunnelProjectId: line.jobFunnelProjectId,
      monthlyMargin: line.monthlyMargin,
      monthlyPrime: line.monthlyPrime,
      splitRate: line.splitRate,
      commissionRate: line.commissionRate,
    };
    if (isMissionPrimeLine(line)) {
      return {
        ...base,
        additionalData: {
          normalWorkedDays: line.normalWorkedDays,
          saturdaysWorkedDays: line.saturdaysWorkedDays,
          sundayAndholidaysWorkedDays: line.sundayAndholidaysWorkedDays,
          astreinteWorkedDays: line.astreinteWorkedDays,
        },
      };
    }
    return base;
  }),
});
